import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, signal, computed, effect, OnInit, Renderer2, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  hasPreloadedValue as fieldHasValue,
  normalizeSelectValue,
  parseSelectEmitValue,
  resolveFieldEnabled,
  resolveOptions,
} from '../utils/field-preload.utils';
import { LockToggleComponent } from '../lock-toggle/lock-toggle.component';
import { FieldErrorTimeoutService } from '../../services/field-error-timeout.service';

@Component({
  selector: 'app-selected',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, LockToggleComponent],
  templateUrl: './selected.component.html',
  styleUrl: './selected.component.css',
})
export class SelectedComponent implements OnInit, AfterViewInit, OnDestroy {

  private lockTouched = false;

  @Input({ required: true })
  set config(value: any) {
    const showBlocker = value.showBlocker ?? false;
    this.configSignal.set({
      label: value.label || 'Seleccione',
      placeholder: value.placeholder || 'Seleccione una opción',
      defaultValue: value.defaultValue ?? '',
      enabled: resolveFieldEnabled({
        showBlocker,
        lockTouched: this.lockTouched,
        currentEnabled: this.configSignal().enabled,
        requestedEnabled: value.enabled,
        defaultValue: value.defaultValue,
        editedByUser: this.selectionFromUser,
      }),
      options: value.options || [],
      validation: value.validation || null,
      formName: value.formName || null,
      textError: value.textError || 'Este campo es requerido.',
      showBlocker,
      originalValue: value.originalValue ?? value.defaultValue ?? null,
      isDirty: value.isDirty ?? false,
      isValid: value.isValid ?? true,
    });
  }

  @Input() timeoutSeconds: number = 8;

  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  configSignal = signal<any>({
    label: 'Seleccione',
    placeholder: 'Seleccione una opción',
    defaultValue: '',
    enabled: true,
    options: [],
    validation: null,
    formName: null,
    textError: 'Este campo es requerido.',
    showBlocker: false,
    originalValue: null,
    isDirty: false,
    isValid: true,
  });

  enabled = computed(() => this.configSignal().enabled);
  currentValue = computed(() => this.configSignal().defaultValue);
  optionsList = computed(() => resolveOptions(this.configSignal().options));
  showBlockerIcon = computed(() => this.configSignal().showBlocker === true);
  showError = computed(() => {
    const control = this.form.get('selected');
    return this.offTimeOut() && this.enabled() && !!control?.invalid
      && (control?.value === '' || control?.value === null || control?.value === undefined);
  });

  compareSelectValues = (a: unknown, b: unknown): boolean => a == b;

  form: FormGroup;
  offTimeOut = signal(false);
  hasWritten = signal(false);
  lastEmittedValue = signal<any>(null);
  private isUpdating = false;
  private selectionFromUser = false;
  private viewInitialized = false;
  private debounceTimer: any;
  private timeoutHandle: any;

  @Output() selectValue = new EventEmitter<any>();
  @Output() dirtyChange = new EventEmitter<{ formName: string, isDirty: boolean, isValid: boolean }>();
  @Output() enabledChange = new EventEmitter<{ formName: string; enabled: boolean }>();

  constructor(
    private renderer: Renderer2,
    private fieldErrorTimeout: FieldErrorTimeoutService
  ) {
    this.form = new FormGroup({
      selected: new FormControl('', Validators.required)
    });

    effect(() => {
      const control = this.form.get('selected');
      if (this.enabled()) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }

      if (this.viewInitialized) {
        setTimeout(() => this.updateStylesByState(), 0);
      }
    });

    effect(() => {
      const options = this.optionsList();
      const defaultValue = this.currentValue();
      const hasDefault = fieldHasValue(defaultValue) || defaultValue === false || defaultValue === 0;

      if (this.hasWritten() && !hasDefault) {
        return;
      }

      if (this.selectionFromUser && defaultValue == this.configSignal().originalValue) {
        return;
      }

      if (!hasDefault) {
        const control = this.form.get('selected');
        if (control?.value !== '' && control?.value !== null && control?.value !== undefined) {
          this.applySelectValue('');
        } else {
          this.startTimeout();
        }
        return;
      }

      const normalized = normalizeSelectValue(defaultValue, options);
      this.applySelectValue(normalized);
    });

    effect(() => {
      const control = this.form.get('selected');
      const validations = this.configSignal().validation;
      control?.setValidators(Validators.compose(validations ?? [Validators.required]));
      control?.updateValueAndValidity({ emitEvent: false });
    });

    this.form.get('selected')?.valueChanges.subscribe((val) => {
      if (this.isUpdating) return;

      const formName = this.configSignal().formName;
      const originalValue = this.configSignal().originalValue;
      const isValid = this.form.valid;
      const isDirty = val !== '' && val !== null && val !== undefined && val != originalValue;

      if (val !== '' && val !== null && val !== undefined) {
        this.hasWritten.set(true);
        this.selectionFromUser = true;
        this.offTimeOut.set(false);
        clearTimeout(this.timeoutHandle);
        setTimeout(() => this.updateStylesByState(), 0);
        this.resetDebounce();
      } else {
        this.hasWritten.set(false);
        this.selectionFromUser = false;
        this.startTimeout();
        setTimeout(() => this.updateStylesByState(), 0);
      }

      if (isDirty !== this.configSignal().isDirty) {
        this.configSignal.update(config => ({
          ...config,
          isDirty: isDirty,
          isValid: isValid
        }));
        this.dirtyChange.emit({ formName, isDirty, isValid });
      }
    });
  }

  ngOnInit() {
    this.startTimeout();
  }

  private applySelectValue(value: unknown): void {
    const control = this.form.get('selected');
    const controlValue = this.toSelectControlValue(value);

    if (!control) {
      return;
    }

    const hasMatchingOption = (fieldHasValue(controlValue) || controlValue === 0 || controlValue === false)
      && this.optionsList().some((option) => option.value == controlValue);

    if (control.value == controlValue) {
      if (!hasMatchingOption || this.selectionFromUser) {
        return;
      }

      this.isUpdating = true;
      control.reset('', { emitEvent: false });
      control.setValue(controlValue, { emitEvent: false });
      setTimeout(() => {
        this.isUpdating = false;
      }, 0);
      return;
    }

    this.selectionFromUser = false;
    this.isUpdating = true;
    control.setValue(controlValue, { emitEvent: false });

    if ((fieldHasValue(value) || value === false || value === 0) && hasMatchingOption) {
      this.hasWritten.set(true);
      this.offTimeOut.set(false);
      clearTimeout(this.timeoutHandle);
      this.lastEmittedValue.set(parseSelectEmitValue(controlValue, this.configSignal().options));
    } else {
      this.hasWritten.set(false);
      this.startTimeout();
    }

    if (this.viewInitialized) {
      setTimeout(() => this.updateStylesByState(), 0);
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 0);
  }

  private toSelectControlValue(value: unknown): unknown {
    const options = this.optionsList();
    const normalized = normalizeSelectValue(value, options);
    const hasBooleanOptions = options.some((opt) => typeof opt.value === 'boolean');

    if (hasBooleanOptions && typeof normalized === 'boolean') {
      return normalized;
    }

    if (options.length > 0 && (fieldHasValue(normalized) || normalized === false || normalized === 0)) {
      const match = options.find((opt) => opt.value == normalized);
      return match ? match.value : normalized;
    }

    return normalized;
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    setTimeout(() => this.updateStylesByState(), 0);
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.timeoutHandle) clearTimeout(this.timeoutHandle);
  }

  toggleEnabled(): void {
    this.lockTouched = true;
    const enabled = !this.configSignal().enabled;
    this.configSignal.update((config) => ({
      ...config,
      enabled
    }));

    if (this.configSignal().formName) {
      this.enabledChange.emit({ formName: this.configSignal().formName!, enabled });
    }
  }

  private startTimeout(): void {
    if (!this.enabled()) return;

    const control = this.form.get('selected');
    if (this.offTimeOut()) {
      if (control?.invalid && (!control.value || control.value === '')) {
        setTimeout(() => this.updateStylesByState(), 0);
      } else {
        this.offTimeOut.set(false);
      }
      return;
    }

    clearTimeout(this.timeoutHandle);
    this.offTimeOut.set(false);

    this.timeoutHandle = setTimeout(() => {
      const currentControl = this.form.get('selected');
      if (currentControl && currentControl.invalid && (!currentControl.value || currentControl.value === '')) {
        this.offTimeOut.set(true);
        setTimeout(() => this.updateStylesByState(), 0);
      }
    }, this.fieldErrorTimeout.delayMs(this.timeoutSeconds));
  }

  resetDebounce(): void {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.emitIfValid();
    }, 100);
  }

  emitIfValid(): void {
    if (!this.hasWritten()) return;

    const valueToEmit = parseSelectEmitValue(
      this.form.value.selected,
      this.configSignal().options
    );
    const hasValueChanged = valueToEmit != this.lastEmittedValue();
    const isValid = this.form.valid;
    const formName = this.configSignal().formName;
    const originalValue = this.configSignal().originalValue;
    const isDirty = valueToEmit !== '' && valueToEmit !== null && valueToEmit !== undefined && valueToEmit != originalValue;

    if (!hasValueChanged) return;

    this.lastEmittedValue.set(valueToEmit);
    this.selectValue.emit({
      value: valueToEmit,
      formName: formName,
      isValid: isValid,
      isDirty: isDirty
    });
  }

  onBlurSelect(): void {
    this.emitIfValid();
    const control = this.form.get('selected');
    if (control?.invalid) {
      this.offTimeOut.set(true);
      this.updateStylesByState();
    }
  }

  private updateStylesByState(): void {
    if (!this.viewInitialized) return;

    const control = this.form.get('selected');
    if (!control) return;

    if (!this.enabled()) {
      this.getStyleTextDisabled();
      return;
    }

    if (control.invalid && this.offTimeOut()) {
      this.getStyleError();
      return;
    }

    this.getStyleEnabled();
  }

  getStyleError(): void {
    if (!this.subtitleLabel?.nativeElement || !this.inbox?.nativeElement) return;
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');
    this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.addClass(this.inbox.nativeElement, 'border-error');
  }

  getStyleEnabled(): void {
    if (!this.subtitleLabel?.nativeElement || !this.inbox?.nativeElement) return;
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-error');
    this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
    this.renderer.addClass(this.inbox.nativeElement, 'border-enabled');
  }

  getStyleTextDisabled(): void {
    if (!this.subtitleLabel?.nativeElement || !this.inbox?.nativeElement) return;
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-error');
    this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
    this.renderer.addClass(this.inbox.nativeElement, 'border-disabled');
  }

  hasError = () => this.showError();
}
